import { Banknote, CalendarDays, MapPin, PencilLine, Tag, User, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { AfterDecision } from '../components/detail/AfterDecision';
import { AgendaOnlyProject } from '../components/detail/AgendaOnlyProject';
import { CloseProjectSection } from '../components/detail/CloseProjectSection';
import { DecisionPanel } from '../components/detail/DecisionPanel';
import { LeadTimeAlert } from '../components/detail/LeadTimeAlert';
import { ProjectTimeline } from '../components/detail/ProjectTimeline';
import { ResponsibleContact } from '../components/detail/ResponsibleContact';
import { SpaceConflictPanel } from '../components/detail/SpaceConflictPanel';
import { DocumentSection } from '../components/DocumentSection';
import { ProjectStatusBadge } from '../components/ProjectStatusBadge';
import { useAgendaProject } from '../hooks/useAgendaProject';
import { useWritableMinistries } from '../hooks/useProjectCatalogs';
import { useProjectContact } from '../hooks/useProjectContact';
import { useProject, useProjectSupportItems } from '../hooks/useProjects';
import { useProjectTimeline } from '../hooks/useProjectTimeline';
import { useSpaceConflicts } from '../hooks/useSpaceConflicts';
import { attributionFor } from '../lib/attribution';
import { hasUnsentAdjustments, isRunningNow } from '../lib/flags';
import { originCrumb, readNavOrigin, visibleNavItems } from '@/shared/constants/navigation';
import { useAuth } from '@/app/providers/AuthProvider';
import { Breadcrumb } from '@/shared/components/Breadcrumb';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { buttonVariants } from '@/shared/components/ui/button';
import { can, type Role } from '@/shared/constants/roles';
import { REVIEW_QUEUE_STATUSES } from '@/shared/constants/statuses';
import { messages } from '@/shared/constants/messages';
import { leadTimeWarning } from '@/shared/constants/projectSizes';
import { formatDate, formatDateTimeRange } from '@/shared/lib/dates';
import { toUserMessage } from '@/shared/lib/errors';
import { formatCurrency, formatList, formatNumber } from '@/shared/lib/format';
import type { ProjectContact, SpaceConflict } from '@/shared/types/domain';

/**
 * Detalhe do projeto — leitura para todo mundo, **decisão para quem tem
 * `reviewProject` com o projeto na fila** (spec-project-review.md §6.4).
 *
 * O painel de decisão mora aqui, e não em `features/review` (decisão 12): é
 * a camada de ação da tela do projeto, onde já vive cancelar.
 *
 * **O documento usa a gramática do FORMULÁRIO** (decisão 18): as mesmas cinco
 * seções, na mesma ordem, com os mesmos títulos. Antes eram três gramáticas na
 * mesma página — grid de fatos sem título, três `Card`, e seções com divisória
 * — e a ordem não era a de quem escreveu: logística e orçamento apareciam
 * ANTES do Objetivo, então quem decide lia o "onde" antes de saber do que se
 * tratava.
 */
export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();
  const { currentUser } = useAuth();
  const { data: project, isPending, error } = useProject(id);
  const { data: supportItems } = useProjectSupportItems(id);
  const timelineQuery = useProjectTimeline(id);
  // Dado pessoal só para quem decide (decisão 30): sem `reviewProject`, a
  // consulta nem sai — a linha do responsável continua sendo só o nome.
  const isReviewerForContact = Boolean(currentUser && can(currentUser.roles, 'reviewProject'));
  const contactQuery = useProjectContact(id, isReviewerForContact);
  /**
   * Os alertas do documento (decisão 36) — e **a mesma consulta que
   * `DecisionPanel` faz**: a chave é `queryKeys.spaceConflicts(id)` nos dois, e
   * o TanStack Query resolve os dois com uma requisição só. Gateado por papel
   * pela mesma razão de `useProjectContact`: sem `reviewProject` a RPC recusaria
   * a consulta, e evitar o disparo evita o erro previsível.
   */
  const conflictsQuery = useSpaceConflicts(id, isReviewerForContact);

  /**
   * A porta do modo ajuste (§3.1) — e o predicado dela **não é**
   * `canActOnMinistry`, que governa "Cancelar" logo abaixo.
   *
   * `currentUser.ministry_ids` vem de `my_ministry_ids()`, que para um
   * coordenador contém a coordenação inteira: ele enxerga aqueles projetos e
   * não pode escrever neles. Reusar aquele predicado aqui daria a ele um botão
   * que leva a um formulário cujo primeiro autosave devolve
   * `not_allowed_for_role`. O espelho certo é `my_writable_ministry_ids()`, o
   * mesmo que o formulário já usa.
   *
   * Só consulta com o projeto devolvido: quem abre um projeto aprovado não
   * paga por uma lista que nenhuma parte da tela vai usar.
   */
  const isReturnedForAdjustment = project?.status === 'changes_requested';
  const writableQuery = useWritableMinistries(isReturnedForAdjustment);

  /**
   * O que aconteceu NESTA sessão com a decisão: nada, decidi, ou perdi a
   * corrida (§2.4, §5.4).
   *
   * Mora na página, e não no painel, porque é o que faz o painel dar lugar ao
   * bloco de volta à fila — inclusive antes de a invalidação do detalhe
   * chegar. Some no F5, e isso é correto: não é estado do projeto, é o rastro
   * da ação que a pessoa acabou de tomar.
   */
  const [outcome, setOutcome] = useState<'mine' | 'lost' | null>(null);

  const roles = currentUser?.roles ?? [];

  if (isPending) return <LoadingState label="Carregando projeto…" />;

  if (error) {
    return <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />;
  }

  /**
   * ⚠️ **O ramo da leitura reduzida** (spec-calendar.md §7.2, decisão 76).
   *
   * ```
   * useProject(id) → achou? → tela completa, como sempre
   *                → null?  → useAgendaProject(id) → achou? → LEITURA REDUZIDA
   *                                                → null?  → "não encontrado"
   * ```
   *
   * A mudança é **aditiva**: nenhum caminho existente muda de comportamento —
   * quem já enxergava o projeto pela RLS continua caindo no primeiro ramo, com
   * descrição, orçamento, apoio, linha do tempo e os verbos do seu papel.
   * Projeto SEU abre inteiro, como sempre abriu (decisão 78).
   *
   * O segundo ramo só existe porque "projeto não encontrado" seria **falso**
   * para quem acabou de ver aquele projeto no calendário da tela anterior.
   */
  if (!project) {
    return <AgendaFallback id={id} roles={roles} state={state} />;
  }

  const running = isRunningNow(project);

  const isReviewer = can(roles, 'reviewProject');
  // Painel de decisão: papel + status na fila (§6.4) + nada decidido nesta
  // sessão. Fora disso a tela é a de sempre — inclusive para o próprio revisor
  // olhando um projeto já decidido.
  const showDecisionPanel =
    isReviewer &&
    outcome === null &&
    (REVIEW_QUEUE_STATUSES as readonly string[]).includes(project.status);

  // Convite de UX para "Cancelar" — a RPC é a autorização real (união de
  // `can_write_ministry` e `can_decide_for_ministry`). No cliente,
  // aproximamos: admin/overseer sempre podem (a RLS já não teria mostrado o
  // projeto se não pudessem); líder/auxiliar só do próprio ministério.
  const canActOnMinistry =
    roles.includes('admin') ||
    roles.includes('overseer') ||
    Boolean(currentUser?.ministry_ids.includes(project.ministry_id));

  // A autorização real continua sendo `can_write_ministry` dentro das RPCs; o
  // botão é convite, como manda a regra 1.
  const canAdjust =
    isReturnedForAdjustment &&
    (writableQuery.data ?? []).some((entry) => entry.ministry_id === project.ministry_id);

  const entries = timelineQuery.data ?? [];
  const lastTransition = [...entries].reverse().find((entry) => entry.kind === 'transition');
  const attribution = attributionFor(
    project,
    lastTransition && lastTransition.kind === 'transition'
      ? {
          actor_profile_id: lastTransition.actor_profile_id,
          actor_name: lastTransition.actor_name,
          created_at: lastTransition.created_at,
        }
      : null,
  );

  /**
   * A trilha é a ABA a que esta tela pertence — e ela é a de ORIGEM quando
   * houve origem (decisão 16). Quem veio da fila lê `Análise › Título` com
   * "Análise" acesa na sidebar; quem veio de `/projetos`, ou colou o link, lê
   * `Projetos › Título`. **A URL é a mesma nos três casos** (decisão 1 da 1ª
   * rodada): duas telas do mesmo projeto divergem em meses.
   *
   * Trilha e sidebar leem a MESMA função, de propósito: uma trilha dizendo
   * "Análise" com a sidebar acesa em "Projetos" é pior do que qualquer um dos
   * dois sozinho.
   */
  const crumb = originCrumb(visibleNavItems(roles), `/projetos/${project.id}`, readNavOrigin(state));

  return (
    <div
      className={
        // `flex flex-col` já em 375px: é o que faz `order-*` valer ANTES de
        // `lg` — sem um container flex/grid ativo, as classes `order-*` não
        // têm efeito nenhum e a coluna cai para o fim do DOM (a ordem em que
        // os dois blocos foram escritos), exatamente o oposto do que a
        // decisão 10 pede para 375px.
        showDecisionPanel || outcome !== null
          ? 'flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-8'
          : ''
      }
    >
      <div className={showDecisionPanel || outcome !== null ? 'order-2 min-w-0 lg:order-1' : undefined}>
        <PageHeader
          breadcrumb={
            <Breadcrumb
              items={[
                ...(crumb ? [{ label: crumb.label, to: crumb.to }] : []),
                { label: project.title ?? 'Rascunho sem título' },
              ]}
            />
          }
          title={project.title ?? 'Rascunho sem título'}
          description={
            <span className="flex flex-col gap-1">
              {/* A categoria saiu daqui: ela já é um fato na seção de
                  Identificação logo abaixo, e dizê-la duas vezes em três
                  centímetros de tela gastava a única linha que responde de quem
                  é o projeto. */}
              <span className="flex flex-wrap items-center gap-x-1.5">
                <Link
                  to={`/ministerios/${project.ministry_id}`}
                  className="rounded font-medium text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {project.ministry_name}
                </Link>
                <span aria-hidden="true" className="text-border">
                  ·
                </span>
                <Link
                  to={`/coordenacoes/${project.coordination_id}`}
                  className="rounded hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {project.coordination_name}
                </Link>
              </span>
              {/* A atribuição da decisão (decisão 14) — quem produziu o
                  estado ATUAL, de relance, sem rolar até o fim da linha do
                  tempo. É ela que responde "quem decidiu primeiro?" quando
                  duas pessoas clicam ao mesmo tempo (§5.4). */}
              {attribution ? <span>{attribution}</span> : null}
            </span>
          }
          actions={
            /* UM crachá, e não dois (decisão 29): "Aguardando relatório" é
               `approved` visto depois da data, e `ProjectStatusBadge` já o
               resolve. "Em execução" continua ao lado porque responde outra
               pergunta — está acontecendo AGORA.

               ⚠️ **Zero crachás enquanto o painel de decisão está na tela**
               (decisão 35): para quem está decidindo, "Em análise" é
               irrelevante — é a razão de a tela estar aberta. A condição é a
               MESMA que governa o painel, e não `location.state.from`: origem
               de navegação morre no F5 (2ª rodada §2.2 / ADR-012), e um crachá
               que reaparece ao recarregar é a mesma classe de bug que os botões
               que sumiam.

               O que a regra preserva sem exceção nenhuma: "Aguardando
               relatório" é `approved`, que não está na fila; `draft` nunca
               esteve na fila e é a única situação sem linha de atribuição; e
               para o líder nada muda, porque o painel não existe para ele. */
            <div className="flex flex-wrap items-center gap-1.5">
              {running ? <Badge variant="primary">Em execução</Badge> : null}
              {showDecisionPanel ? null : <ProjectStatusBadge project={project} />}
              {/* A porta (decisões 42, 43) — e o crachá ao lado dela fica
                  INTACTO. A regra 35 (crachá some) vale só enquanto o painel de
                  decisão está na tela, e o painel não existe em
                  `changes_requested`. Para o líder, "Ajustes solicitados" ao
                  lado do botão é metade da frase: ele diz por que o botão está
                  ali. */}
              {canAdjust ? (
                <Link
                  to={`/calendario/${project.id}/editar`}
                  className={buttonVariants({ className: 'ml-1' })}
                >
                  <PencilLine aria-hidden="true" className="size-4" />
                  {hasUnsentAdjustments(project)
                    ? messages.projects.adjustContinue
                    : messages.projects.adjust}
                </Link>
              ) : null}
            </div>
          }
        />

        <ProjectDocument
          project={project}
          contact={contactQuery.data}
          supportItems={supportItems}
          entries={entries}
          isTimelineLoading={timelineQuery.isPending}
          canActOnMinistry={canActOnMinistry}
          conflicts={isReviewer ? (conflictsQuery.data ?? []) : []}
          isConflictsLoading={isReviewer && conflictsQuery.isPending}
          leadTime={
            isReviewer ? leadTimeWarning(project.expected_audience, project.starts_at) : null
          }
        />
      </div>

      {showDecisionPanel ? (
        <div className="order-1 mb-6 lg:order-2 lg:sticky lg:top-20 lg:mb-0">
          <DecisionPanel
            project={project}
            onDecided={() => setOutcome('mine')}
            onLostRace={() => setOutcome('lost')}
          />
        </div>
      ) : null}

      {outcome !== null ? (
        <div className="order-1 mb-6 lg:order-2 lg:sticky lg:top-20 lg:mb-0">
          <AfterDecision
            outcome={outcome}
            attribution={attribution}
            deciderName={
              lastTransition && lastTransition.kind === 'transition'
                ? lastTransition.actor_name
                : null
            }
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * O segundo ramo: a agenda da igreja, ou o vazio de verdade.
 *
 * Componente próprio, e não um `if` dentro da página, por uma razão de hooks: a
 * consulta da leitura reduzida só pode existir **depois** de `useProject` ter
 * voltado `null`, e um hook condicional dentro da página seria erro de regra dos
 * hooks. Aqui ele é incondicional — o componente é que só é montado no caso.
 *
 * Consultar as duas em paralelo teria sido mais simples e faria toda visita a um
 * projeto do próprio ministério pagar uma RPC que ninguém vai ler.
 */
function AgendaFallback({
  id,
  roles,
  state,
}: {
  id: string | undefined;
  roles: readonly Role[];
  state: unknown;
}) {
  const { data: agenda, isPending } = useAgendaProject(id, true);

  if (isPending) return <LoadingState label="Carregando projeto…" />;

  if (agenda) {
    const crumb = originCrumb(
      visibleNavItems(roles),
      `/projetos/${agenda.id}`,
      readNavOrigin(state),
    );
    return <AgendaOnlyProject project={agenda} crumb={crumb} />;
  }

  // Projeto inexistente e projeto fora da FILIAL devolvem a mesma coisa — nada —,
  // e a tela também não os distingue. Dizer "existe, mas você não pode ver"
  // entregaria a existência de um projeto de outra filial.
  return (
    <EmptyState
      title="Projeto não encontrado"
      description={messages.errors.notFound}
      action={
        <Link to="/projetos" className="text-sm font-medium text-primary hover:underline">
          Voltar para a lista de projetos
        </Link>
      }
    />
  );
}

interface ProjectDocumentProps {
  project: NonNullable<ReturnType<typeof useProject>['data']>;
  contact: ProjectContact | null | undefined;
  supportItems: ReturnType<typeof useProjectSupportItems>['data'];
  entries: ReturnType<typeof useProjectTimeline>['data'] & object;
  isTimelineLoading: boolean;
  canActOnMinistry: boolean;
  /** Vazio para quem não decide (decisão 36): a seção fica como sempre foi. */
  conflicts: readonly SpaceConflict[];
  isConflictsLoading: boolean;
  leadTime: ReturnType<typeof leadTimeWarning>;
}

/**
 * O documento, nas cinco seções do formulário e na ordem em que o autor as
 * escreveu (decisão 18): identificação → objetivo → data e local → apoio e
 * custos → informações complementares. Depois vêm a linha do tempo e a saída.
 *
 * **Os alertas de quem decide moram DENTRO dele** (decisão 36), cada um ao lado
 * do fato que qualifica. Para quem não decide eles chegam vazios e a seção é a
 * de sempre.
 *
 * **Seção vazia continua aparecendo**, com "Ainda não preenchido" — é o que a
 * US-19 pede de quem decide ("todos os campos submetidos, incluindo os
 * vazios"). Esconder uma seção sem conteúdo faria o revisor confundir "não
 * preencheu" com "a seção nem existe".
 */
function ProjectDocument({
  project,
  contact,
  supportItems,
  entries,
  isTimelineLoading,
  canActOnMinistry,
  conflicts,
  isConflictsLoading,
  leadTime,
}: ProjectDocumentProps) {
  return (
    <div className="min-w-0 space-y-8">
      <DocumentSection id="identificacao" title={messages.projectSections.identity}>
        <Facts>
          <Fact icon={<Tag className="size-4" />} label="Categoria">
            {project.category_name ?? <Unfilled />}
          </Fact>
          <Fact icon={<User className="size-4" />} label="Responsável">
            {project.responsible_name ?? <Unfilled />}
            {/* Contato embaixo do nome, e só para quem decide (decisão 30). */}
            {project.responsible_name ? <ResponsibleContact contact={contact} /> : null}
          </Fact>
          <Fact icon={<Users className="size-4" />} label="Público esperado">
            {project.expected_audience !== null ? (
              `${formatNumber(project.expected_audience)} pessoas`
            ) : (
              <Unfilled />
            )}
          </Fact>
        </Facts>
      </DocumentSection>

      <DocumentSection id="objetivo" title={messages.projectSections.objective}>
        <p className="whitespace-pre-line text-sm leading-relaxed">
          {project.description || <Unfilled />}
        </p>
      </DocumentSection>

      {/* ⚠️ A âncora acompanhou o título (decisão 37): era `quando-e-onde`.
          Âncora que descreve o título antigo é ponteiro quebrado no dia em que
          alguém linkar a seção. */}
      <DocumentSection id="data-e-local" title={messages.projectSections.schedule}>
        {/* Um fato por LINHA, e não dois lado a lado num grid: é o empilhamento
            que abre a coluna da direita para o alerta correspondente. */}
        <dl className="space-y-4">
          <FactRow
            icon={<CalendarDays className="size-4" />}
            label={messages.projects.factDateTime}
            alert={leadTime ? <LeadTimeAlert {...leadTime} /> : null}
          >
            {project.starts_at && project.ends_at ? (
              formatDateTimeRange(project.starts_at, project.ends_at)
            ) : (
              <Unfilled />
            )}
          </FactRow>
          <FactRow
            icon={<MapPin className="size-4" />}
            label={messages.projects.factPlace}
            alert={<SpaceConflictPanel conflicts={conflicts} isLoading={isConflictsLoading} />}
          >
            {project.space_names.length > 0 ? formatList(project.space_names) : <Unfilled />}
          </FactRow>
        </dl>
      </DocumentSection>

      <DocumentSection id="apoio-e-custos" title={messages.projectSections.support}>
        <div className="space-y-5">
          {supportItems && supportItems.length > 0 ? (
            <ul className="divide-y divide-border">
              {supportItems.map((item) => (
                <li key={item.id} className="flex flex-col gap-1 py-3 first:pt-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{item.support_type_name}</span>
                    <Badge variant="muted">{messages.supportGroups[item.group_name]}</Badge>
                    {item.needed_by ? (
                      <span className="text-xs text-muted-foreground">
                        até {formatDate(item.needed_by)}
                      </span>
                    ) : null}
                  </div>
                  {item.request ? (
                    <p className="text-sm text-muted-foreground">{item.request}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum apoio solicitado.</p>
          )}

          <Facts>
            {/* ADR-011: número declarado, para planejar e ter histórico. O
                sistema não é o financeiro da igreja e nunca concilia com ele. */}
            <Fact icon={<Banknote className="size-4" />} label="Orçamento estimado">
              {project.estimated_budget_cents !== null ? (
                formatCurrency(project.estimated_budget_cents)
              ) : (
                <Unfilled />
              )}
              {project.funding_source_name ? (
                <span className="block text-xs font-normal text-muted-foreground">
                  {project.funding_source_name}
                </span>
              ) : null}
            </Fact>
          </Facts>
        </div>
      </DocumentSection>

      <DocumentSection id="informacoes-complementares" title={messages.projectSections.extraInfo}>
        <p className="whitespace-pre-line text-sm leading-relaxed">
          {project.extra_info || <Unfilled />}
        </p>
      </DocumentSection>

      {entries.length > 0 || isTimelineLoading ? (
        <DocumentSection id="linha-do-tempo" title={messages.projectSections.timeline}>
          <ProjectTimeline entries={entries} isLoading={isTimelineLoading} />
        </DocumentSection>
      ) : null}

      <CloseProjectSection project={project} canAct={canActOnMinistry} />
    </div>
  );
}

function Facts({ children }: { children: ReactNode }) {
  return <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>;
}

interface FactProps {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}

/**
 * Um fato do documento — rótulo apagado em cima, valor embaixo.
 *
 * ⚠️ **Sem caixa, e a perda dela é o ponto** (decisão 18). Cada fato já foi
 * `rounded-lg border bg-card`: mini-cartão dentro de uma seção com divisória é
 * moldura sobre moldura, e o resultado eram seis retângulos disputando a
 * atenção do olho antes de qualquer palavra ser lida. **O ícone fica** — é ele
 * que permite varrer o bloco sem ler rótulo por rótulo.
 */
function Fact({ icon, label, children }: FactProps) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span aria-hidden="true" className="shrink-0">
          {icon}
        </span>
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * Um fato com espaço à direita para o alerta que o qualifica (decisão 36).
 *
 * **Em 375px o alerta cai ABAIXO do fato**, na mesma ordem de leitura do
 * desktop — nada de coluna lateral no celular. É `sm:` e não `lg:` de
 * propósito: o documento ocupa a largura toda em tablet, e ali as duas colunas
 * já cabem sem espremer nenhuma das duas.
 *
 * `alert` nulo (ou um componente que decide não renderizar nada, como
 * `SpaceConflictPanel` sem conflito) devolve uma célula vazia, e o fato ocupa a
 * linha como ocuparia sozinho — o grid não deixa buraco visível.
 *
 * **Quem sobra com a largura é o ALERTA, não o fato.** O fato é curto por
 * natureza ("03/08/2026, 19:30 às 22:30", "Salão Social") e a coluna larga só
 * lhe rendia espaço vazio, enquanto o alerta espremido em 22rem estourava para
 * quatro linhas e empurrava "Local" para longe de "Data e Hora". Coluna do fato
 * fixa (as duas linhas alinham a borda esquerda dos alertas, que é o que faz
 * eles lerem como uma coluna) e o resto para o alerta.
 */
function FactRow({ icon, label, children, alert }: FactProps & { alert: ReactNode }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:items-start sm:gap-6">
      <Fact icon={icon} label={label}>
        {children}
      </Fact>
      {alert}
    </div>
  );
}

/** US-19: campo vazio aparece dito, nunca omitido. */
function Unfilled() {
  return <span className="font-normal text-muted-foreground">Ainda não preenchido</span>;
}
